<script setup>
/**
 * PipelineOpportunityDialog — modal de resumo de uma oportunidade do funil.
 *
 * É alimentado pelos dados que o chamador já tem (não faz fetch próprio):
 *  - no board, pelo card single-stage (PipelineBoard/PipelineCard);
 *  - no painel do contato, por um item de `opportunities` (DevKitConversationPipeline).
 *
 * Por isso a prop `opportunity` é um shape NORMALIZADO e tolerante: cada origem
 * passa os campos que possui. Campos ausentes simplesmente não renderizam a
 * respectiva seção. Reusa o Dialog do design-system (teleport + scrim + Esc)
 * para herdar o comportamento padrão de modais do Chatwoot.
 *
 * Shape esperado de `opportunity` (todos opcionais exceto display_id):
 *   {
 *     display_id, contact_name, channel_type,
 *     stage_name, behavior ('open'|'won'|'lost'), probability,
 *     value, assignee ({ name } | null), priority,
 *     last_activity_at, labels (string[])
 *   }
 */
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUnixTime } from 'date-fns';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { formatMoney } from './utils/currency';

const props = defineProps({
  opportunity: {
    type: Object,
    default: null,
  },
  // Permite editar o valor inline a partir do modal (board). Quando false,
  // o valor é apenas exibido (ex.: aberto pelo painel do contato).
  editableValue: {
    type: Boolean,
    default: false,
  },
  // Estágios-alvo para os atalhos "Marcar Ganho/Perdido" (opcionais).
  wonStage: {
    type: Object,
    default: null,
  },
  lostStage: {
    type: Object,
    default: null,
  },
  // Contato dono da oportunidade (vem do painel do contato). Habilita o botão
  // "Ver no funil" (filtra o board por contato) e vincula o lembrete ao contato.
  contactId: {
    type: [Number, String],
    default: undefined,
  },
  // Mostra o botão "Ver no funil" no rodapé (só faz sentido fora do board).
  showViewPipeline: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'viewConversation',
  'viewPipeline',
  'setValue',
  'markStage',
  'close',
]);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close, dialogRef });

const opportunity = computed(() => props.opportunity || {});

// --- Cabeçalho -------------------------------------------------------------
const channelInbox = computed(() =>
  opportunity.value.channel_type
    ? { channel_type: opportunity.value.channel_type }
    : null
);

const displayId = computed(() => opportunity.value.display_id);

const headerTitle = computed(() =>
  `#${opportunity.value.display_id ?? ''} ${opportunity.value.contact_name || ''}`.trim()
);

// --- Estágio (chip com cor por behavior) -----------------------------------
const BEHAVIOR_ACCENTS = {
  won: { icon: 'i-lucide-trophy', chipClass: 'text-n-teal-11 bg-n-teal-3' },
  lost: { icon: 'i-lucide-x-circle', chipClass: 'text-n-ruby-11 bg-n-ruby-3' },
  open: {
    icon: 'i-lucide-circle-dot',
    chipClass: 'text-n-slate-11 bg-n-alpha-2',
  },
};
const behavior = computed(() => opportunity.value.behavior || 'open');
const behaviorAccent = computed(
  () => BEHAVIOR_ACCENTS[behavior.value] || BEHAVIOR_ACCENTS.open
);

// Rótulo do behavior via chaves de i18n ESTÁTICAS (o lint de dynamic-keys do
// vue-i18n só aceita literais em t()). Usado como fallback do nome do estágio.
const behaviorLabel = computed(() => {
  if (behavior.value === 'won') return t('PIPELINES.BEHAVIOR.WON');
  if (behavior.value === 'lost') return t('PIPELINES.BEHAVIOR.LOST');
  return t('PIPELINES.BEHAVIOR.OPEN');
});

const stageName = computed(() => opportunity.value.stage_name || '');
const hasProbability = computed(
  () =>
    opportunity.value.probability !== null &&
    opportunity.value.probability !== undefined
);

// --- Valor do negócio ------------------------------------------------------
const numericValue = computed(() => Number(opportunity.value.value) || 0);
const hasValue = computed(() => numericValue.value > 0);
const formattedValue = computed(() => formatMoney(numericValue.value));

const isEditingValue = ref(false);
const valueDraft = ref('');
const valueInput = ref(null);

const startEditValue = async () => {
  if (!props.editableValue) return;
  valueDraft.value = hasValue.value ? String(numericValue.value) : '';
  isEditingValue.value = true;
  await nextTick();
  valueInput.value?.focus();
  valueInput.value?.select();
};

const cancelEditValue = () => {
  isEditingValue.value = false;
};

const commitValue = () => {
  if (!isEditingValue.value) return;
  isEditingValue.value = false;
  const parsed = Number(valueDraft.value);
  const next = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  if (next === numericValue.value) return;
  emit('setValue', { opportunity: props.opportunity, value: next });
};

// --- Responsável -----------------------------------------------------------
const assignee = computed(() => opportunity.value.assignee || null);

// --- Prioridade ------------------------------------------------------------
// Resolvido com chaves de i18n ESTÁTICAS (sem template-literal/variável em t())
// para não disparar o lint de dynamic-keys do vue-i18n.
const priorityLabel = computed(() => {
  switch (opportunity.value.priority) {
    case CONVERSATION_PRIORITY.URGENT:
      return t('PIPELINES.DIALOG.PRIORITY_URGENT');
    case CONVERSATION_PRIORITY.HIGH:
      return t('PIPELINES.DIALOG.PRIORITY_HIGH');
    case CONVERSATION_PRIORITY.MEDIUM:
      return t('PIPELINES.DIALOG.PRIORITY_MEDIUM');
    case CONVERSATION_PRIORITY.LOW:
      return t('PIPELINES.DIALOG.PRIORITY_LOW');
    default:
      return t('PIPELINES.DIALOG.PRIORITY_NONE');
  }
});

// --- Última atividade ("há X") --------------------------------------------
const lastActivityLabel = computed(() => {
  if (!opportunity.value.last_activity_at) return '';
  const unix = getUnixTime(new Date(opportunity.value.last_activity_at));
  if (Number.isNaN(unix)) return '';
  return shortTimestamp(dynamicTime(unix), true);
});

// --- Etiquetas -------------------------------------------------------------
const labels = computed(() => opportunity.value.labels || []);

// --- Ações -----------------------------------------------------------------
const onViewConversation = () => {
  emit('viewConversation', displayId.value);
};

const onMarkStage = stage => {
  if (!stage) return;
  emit('markStage', { opportunity: props.opportunity, toStage: stage });
};

const onViewPipeline = () => emit('viewPipeline', props.opportunity);

// --- Lembrete --------------------------------------------------------------
// Form inline auto-contido: título + datetime-local. Ao salvar, despacha
// `reminders/create` (já escopado por conta) vinculando a oportunidade pela
// conversa (`conversation_display_id`) e, quando houver, pelo contato. O
// datetime-local vem em horário LOCAL sem timezone — convertemos p/ ISO (UTC)
// para o backend. Em sucesso limpamos o form; em erro mantemos o que foi
// digitado para o usuário tentar de novo.
const reminderTitle = ref('');
const reminderDueAt = ref('');
const isCreatingReminder = ref(false);

const canSubmitReminder = computed(
  () =>
    reminderTitle.value.trim().length > 0 &&
    !!reminderDueAt.value &&
    !isCreatingReminder.value
);

// 'YYYY-MM-DDTHH:mm' (sem timezone) -> ISO em UTC. Inválido/vazio -> null.
const toIsoDueAt = localValue => {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const resetReminderForm = () => {
  reminderTitle.value = '';
  reminderDueAt.value = '';
};

const submitReminder = async () => {
  if (!canSubmitReminder.value) return;
  const dueAt = toIsoDueAt(reminderDueAt.value);
  if (!dueAt) {
    useAlert(t('PIPELINES.DIALOG.REMINDER.ERROR'));
    return;
  }
  isCreatingReminder.value = true;
  try {
    await store.dispatch('reminders/create', {
      title: reminderTitle.value.trim(),
      due_at: dueAt,
      conversation_display_id: displayId.value || null,
      // contact_id é opcional no backend; só vai quando o modal o conhece.
      ...(props.contactId ? { contact_id: props.contactId } : {}),
    });
    useAlert(t('PIPELINES.DIALOG.REMINDER.SUCCESS'));
    resetReminderForm();
  } catch (error) {
    useAlert(t('PIPELINES.DIALOG.REMINDER.ERROR'));
  } finally {
    isCreatingReminder.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="$emit('close')"
  >
    <template #default>
      <div v-if="opportunity" class="flex flex-col gap-5">
        <!-- Cabeçalho: ícone do canal + #display_id contato -->
        <div class="flex items-center gap-2.5">
          <ChannelIcon
            v-if="channelInbox"
            :inbox="channelInbox"
            use-brand-icon
            class="flex-shrink-0 size-5"
          />
          <h3
            class="flex-grow min-w-0 text-base font-medium truncate text-n-slate-12"
          >
            {{ headerTitle }}
          </h3>
        </div>

        <!-- Estágio + probabilidade -->
        <div class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('PIPELINES.DIALOG.STAGE') }}
          </span>
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex items-center gap-1.5 px-2 py-1 text-sm font-medium rounded-md"
              :class="behaviorAccent.chipClass"
            >
              <span class="size-3.5" :class="behaviorAccent.icon" />
              {{ stageName || behaviorLabel }}
            </span>
            <span
              v-if="hasProbability"
              class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
              :title="t('PIPELINES.DIALOG.PROBABILITY')"
            >
              {{ `${opportunity.probability}%` }}
            </span>
          </div>
        </div>

        <!-- Valor do negócio (destaque, editável quando permitido) -->
        <div class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('PIPELINES.DIALOG.VALUE') }}
          </span>
          <div v-if="isEditingValue" class="flex items-center gap-2">
            <input
              ref="valueInput"
              v-model="valueDraft"
              type="number"
              min="0"
              step="0.01"
              class="w-32 px-2 py-1 text-lg font-semibold rounded-md outline outline-1 outline-n-brand bg-n-background text-n-slate-12"
              :aria-label="t('PIPELINES.DIALOG.VALUE_LABEL')"
              @keydown.enter.prevent="commitValue"
              @keydown.esc.prevent="cancelEditValue"
              @blur="commitValue"
            />
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-x"
              :aria-label="t('PIPELINES.DIALOG.VALUE_CANCEL')"
              @click="cancelEditValue"
            />
          </div>
          <button
            v-else-if="hasValue && editableValue"
            type="button"
            class="self-start inline-flex items-center gap-2 text-2xl font-semibold cursor-pointer tabular-nums text-n-teal-11 hover:text-n-teal-10"
            :title="t('PIPELINES.DIALOG.VALUE_EDIT')"
            @click="startEditValue"
          >
            <span class="size-5 i-lucide-circle-dollar-sign" />
            {{ formattedValue }}
          </button>
          <span
            v-else-if="hasValue"
            class="inline-flex items-center self-start gap-2 text-2xl font-semibold tabular-nums text-n-teal-11"
          >
            <span class="size-5 i-lucide-circle-dollar-sign" />
            {{ formattedValue }}
          </span>
          <button
            v-else-if="editableValue"
            type="button"
            class="self-start inline-flex items-center gap-1.5 text-sm text-n-slate-10 hover:text-n-brand"
            @click="startEditValue"
          >
            <span class="size-4 i-lucide-circle-dollar-sign" />
            {{ t('PIPELINES.DIALOG.VALUE_ADD') }}
          </button>
          <span
            v-else
            class="text-2xl font-semibold tabular-nums text-n-slate-10"
          >
            {{ formattedValue }}
          </span>
        </div>

        <!-- Grade de meta: responsável + prioridade + última atividade -->
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1.5">
            <span
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('PIPELINES.DIALOG.ASSIGNEE') }}
            </span>
            <span class="flex items-center gap-2 text-sm text-n-slate-12">
              <template v-if="assignee">
                <Avatar :name="assignee.name" :size="20" rounded-full />
                <span class="truncate">{{ assignee.name }}</span>
              </template>
              <template v-else>
                <span class="size-4 i-lucide-user-round text-n-slate-9" />
                <span class="text-n-slate-10">
                  {{ t('PIPELINES.DIALOG.UNASSIGNED') }}
                </span>
              </template>
            </span>
          </div>

          <div class="flex flex-col gap-1.5">
            <span
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('PIPELINES.DIALOG.PRIORITY') }}
            </span>
            <span
              class="flex items-center gap-1.5 text-sm text-n-slate-12 capitalize"
            >
              <span class="size-4 i-lucide-signal text-n-slate-10" />
              {{ priorityLabel }}
            </span>
          </div>

          <div v-if="lastActivityLabel" class="flex flex-col gap-1.5">
            <span
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('PIPELINES.DIALOG.LAST_ACTIVITY') }}
            </span>
            <span class="flex items-center gap-1.5 text-sm text-n-slate-12">
              <span class="size-4 i-lucide-clock text-n-slate-10" />
              {{ lastActivityLabel }}
            </span>
          </div>
        </div>

        <!-- Etiquetas -->
        <div v-if="labels.length" class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('PIPELINES.DIALOG.LABELS') }}
          </span>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="label in labels"
              :key="label"
              class="px-1.5 py-0.5 text-xs rounded text-n-slate-11 bg-n-alpha-2"
            >
              {{ label }}
            </span>
          </div>
        </div>

        <!-- Lembrete: cria um lembrete vinculado à oportunidade (conversa). -->
        <form
          class="flex flex-col gap-2 pt-4 border-t border-n-weak"
          @submit.prevent="submitReminder"
        >
          <span
            class="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >
            <span class="size-3.5 i-lucide-alarm-clock" />
            {{ t('PIPELINES.DIALOG.REMINDER.TITLE') }}
          </span>
          <input
            v-model="reminderTitle"
            type="text"
            :placeholder="t('PIPELINES.DIALOG.REMINDER.TITLE_PLACEHOLDER')"
            :aria-label="t('PIPELINES.DIALOG.REMINDER.TITLE_PLACEHOLDER')"
            class="px-2.5 py-1.5 text-sm rounded-md outline outline-1 outline-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
          <div class="flex flex-wrap items-center gap-2">
            <input
              v-model="reminderDueAt"
              type="datetime-local"
              :aria-label="t('PIPELINES.DIALOG.REMINDER.DUE_AT')"
              class="flex-grow px-2.5 py-1.5 text-sm rounded-md outline outline-1 outline-n-weak bg-n-background text-n-slate-12 focus:outline-n-brand"
            />
            <Button
              type="submit"
              color="blue"
              size="sm"
              icon="i-lucide-bell-plus"
              :label="t('PIPELINES.DIALOG.REMINDER.SUBMIT')"
              :is-loading="isCreatingReminder"
              :disabled="!canSubmitReminder"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between w-full gap-3">
        <div class="flex items-center gap-2">
          <Button
            v-if="wonStage && behavior !== 'won'"
            variant="faded"
            color="teal"
            size="sm"
            icon="i-lucide-trophy"
            :label="t('PIPELINES.DIALOG.MARK_WON')"
            @click="onMarkStage(wonStage)"
          />
          <Button
            v-if="lostStage && behavior !== 'lost'"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-x-circle"
            :label="t('PIPELINES.DIALOG.MARK_LOST')"
            @click="onMarkStage(lostStage)"
          />
        </div>
        <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
          <Button
            v-if="showViewPipeline"
            variant="faded"
            color="slate"
            icon="i-lucide-workflow"
            :label="t('PIPELINES.DIALOG.VIEW_PIPELINE')"
            @click="onViewPipeline"
          />
          <Button
            color="blue"
            icon="i-lucide-messages-square"
            :label="t('PIPELINES.DIALOG.VIEW_CONVERSATION')"
            @click="onViewConversation"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
